import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Card,
  FavoriteToggle,
  handlePageClick,
  Layout,
  ModalV2,
  Pagination,
  Tabs,
  toast,
} from '@utilities';
import { BookmarkIcon } from '@utilities/icons';
import {
  createVideoFavorite,
  deleteVideoFavorite,
  listVideoBookmarksSurvey,
  listVideoFavoritesSurvey,
} from '@api/instant_survey';

import VideoPlayer from './components/VideoPlayer';
import { formatTimestamp } from './utilities/helpers';

import styles from './_index.module.scss';

const PAGE_SIZE = 10;

const Video = ({ data, question, renderResponseTextSummary, surveyRef }) => {
  const [pagesByTab, setPagesByTab] = useState({});
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(false);
  const [hasAttemptedFetchBookmarks, setHasAttemptedFetchBookmarks] = useState(false);
  const [expandedBookmarks, setExpandedBookmarks] = useState({});
  const [favorites, setFavorites] = useState([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [hasAttemptedFetchFavorites, setHasAttemptedFetchFavorites] = useState(false);

  const segments = data?.segments || [];
  const [selectedCompareBy, setSelectedCompareBy] = useState(segments[0]?.quotaGroupRef ?? null);

  // Reset state when surveyRef changes to prevent stale data
  useEffect(() => {
    setBookmarks([]);
    setFavorites([]);
    setHasAttemptedFetchBookmarks(false);
    setHasAttemptedFetchFavorites(false);
  }, [surveyRef]);

  // Fetch bookmarks when component mounts or surveyRef changes
  useEffect(() => {
    if (surveyRef && !hasAttemptedFetchBookmarks && !isLoadingBookmarks) {
      const abortController = new AbortController();
      setIsLoadingBookmarks(true);
      listVideoBookmarksSurvey({ signal: abortController.signal, surveyRef })
        .then((response) => {
          const results = response?.results || response || [];
          setBookmarks(results);
        })
        .catch((error) => {
          if (error.name === 'AbortError' || error.name === 'CanceledError') {
            return;
          }
          toast.error('Failed to load bookmarks');
          setBookmarks([]);
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsLoadingBookmarks(false);
            setHasAttemptedFetchBookmarks(true);
          }
        });

      return () => {
        abortController.abort();
      };
    }
  }, [surveyRef, hasAttemptedFetchBookmarks]);

  // Fetch favorites when component mounts or surveyRef changes
  useEffect(() => {
    if (surveyRef && !hasAttemptedFetchFavorites && !isLoadingFavorites) {
      const abortController = new AbortController();
      setIsLoadingFavorites(true);
      listVideoFavoritesSurvey({ signal: abortController.signal, surveyRef })
        .then((response) => {
          const results = response?.results || response || [];
          setFavorites(results);
        })
        .catch((error) => {
          // Ignore abort errors
          if (error.name === 'AbortError' || error.name === 'CanceledError') {
            return;
          }
          toast.error('Failed to load favorites');
          setFavorites([]);
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsLoadingFavorites(false);
            setHasAttemptedFetchFavorites(true);
          }
        });

      return () => {
        abortController.abort();
      };
    }
  }, [surveyRef, hasAttemptedFetchFavorites]);

  // Refetch bookmarks when a bookmark is added or deleted
  const handleBookmarkChange = () => {
    if (surveyRef) {
      const abortController = new AbortController();
      setIsLoadingBookmarks(true);
      listVideoBookmarksSurvey({ signal: abortController.signal, surveyRef })
        .then((response) => {
          const results = response?.results || response || [];
          setBookmarks(results);
        })
        .catch((error) => {
          // Ignore abort errors
          if (error.name === 'AbortError' || error.name === 'CanceledError') {
            return;
          }
          toast.error('Failed to load bookmarks');
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsLoadingBookmarks(false);
          }
        });
    }
  };

  // Refetch favorites when a favorite is added or deleted
  const handleFavoriteChange = () => {
    if (surveyRef) {
      const abortController = new AbortController();
      setIsLoadingFavorites(true);
      listVideoFavoritesSurvey({ signal: abortController.signal, surveyRef })
        .then((response) => {
          const results = response?.results || response || [];
          setFavorites(results);
        })
        .catch((error) => {
          // Ignore abort errors
          if (error.name === 'AbortError' || error.name === 'CanceledError') {
            return;
          }
          toast.error('Failed to load favorites');
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsLoadingFavorites(false);
          }
        });
    }
  };

  // Handle toggle favorite (create or delete)
  const handleToggleFavorite = async (responseId) => {
    // Check if this response is already favorited
    const existingFavorite = favorites.find(
      (fav) => fav.survey_response === responseId && fav.question_id === question?.id
    );

    try {
      if (existingFavorite) {
        // Delete the favorite
        await deleteVideoFavorite({ favoriteId: existingFavorite.id });
      } else {
        // Create a new favorite
        await createVideoFavorite({
          questionId: question?.id,
          responseId,
        });
      }
      handleFavoriteChange();
    } catch (error) {
      toast.error('Failed to toggle favorite');
    }
  };

  // Create a bookmarks segment by grouping responses with bookmarks
  const bookmarksSegment = useMemo(() => {
    if (selectedCompareBy !== 'bookmarks') {
      return null;
    }

    if (bookmarks.length === 0) {
      return {
        compareBy: 'Bookmarks',
        name: 'Bookmarks',
        quotaGroupRef: 'bookmarks',
        responses: [],
      };
    }

    // Group bookmarks by response ID (filtered to current question only)
    const bookmarksByResponse = bookmarks.reduce((acc, bookmark) => {
      if (bookmark.question_id !== question.id) {
        return acc;
      }
      const responseId = bookmark.survey_response;
      if (!acc[responseId]) {
        acc[responseId] = [];
      }
      acc[responseId].push(bookmark);
      return acc;
    }, {});

    // Get all unique responses with bookmarks from all segments
    const responsesWithBookmarks = [];
    const seenResponseIds = new Set();

    segments.forEach((segment) => {
      segment.responses?.forEach((response) => {
        if (bookmarksByResponse[response.responseId] && !seenResponseIds.has(response.responseId)) {
          responsesWithBookmarks.push({
            ...response,
            bookmarks: bookmarksByResponse[response.responseId],
          });
          seenResponseIds.add(response.responseId);
        }
      });
    });

    return {
      compareBy: 'Bookmarks',
      name: 'Bookmarks',
      quotaGroupRef: 'bookmarks',
      responses: responsesWithBookmarks,
    };
  }, [selectedCompareBy, bookmarks, segments, question]);

  // Create a favorites segment by grouping responses with favorites
  const favoritesSegment = useMemo(() => {
    if (selectedCompareBy !== 'favorites' || favorites.length === 0) {
      return null;
    }

    // Group bookmarks by response ID for showing in favorites tab (filtered to current question only)
    const bookmarksByResponse = bookmarks.reduce((acc, bookmark) => {
      if (bookmark.question_id !== question.id) {
        return acc;
      }
      const responseId = bookmark.survey_response;
      if (!acc[responseId]) {
        acc[responseId] = [];
      }
      acc[responseId].push(bookmark);
      return acc;
    }, {});

    // Create a map of favorites by response ID and question ID
    const favoritesByResponseAndQuestion = favorites.reduce((acc, favorite) => {
      const key = `${favorite.survey_response}_${favorite.question_id}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(favorite);
      return acc;
    }, {});

    // Get all unique responses with favorites from all segments
    const responsesWithFavorites = [];
    const seenResponseIds = new Set();

    segments.forEach((segment) => {
      segment.responses?.forEach((response) => {
        const key = `${response.responseId}_${question?.id}`;
        if (favoritesByResponseAndQuestion[key] && !seenResponseIds.has(response.responseId)) {
          responsesWithFavorites.push({
            ...response,
            bookmarks: bookmarksByResponse[response.responseId] || [],
            favorites: favoritesByResponseAndQuestion[key],
          });
          seenResponseIds.add(response.responseId);
        }
      });
    });

    return {
      compareBy: 'Favorites',
      name: 'Favorites',
      quotaGroupRef: 'favorites',
      responses: responsesWithFavorites,
    };
  }, [selectedCompareBy, favorites, bookmarks, segments, question]);

  const activeSegment =
    selectedCompareBy === 'bookmarks'
      ? bookmarksSegment
      : selectedCompareBy === 'favorites'
        ? favoritesSegment
        : segments.find((segment) => segment.quotaGroupRef === selectedCompareBy) ||
          segments[0] ||
          null;
  const currentPage = pagesByTab[selectedCompareBy] ?? 1;

  const pagedResponses = useMemo(() => {
    const list = activeSegment?.responses ?? [];
    const start = (currentPage - 1) * PAGE_SIZE;
    return list.slice(start, start + PAGE_SIZE);
  }, [activeSegment, currentPage]);

  const totalResponses = activeSegment?.responses?.length ?? 0;
  const pageCount = Math.ceil(totalResponses / PAGE_SIZE);

  useEffect(() => {
    if (!segments.length) {
      setSelectedCompareBy(null);
      return;
    }

    setSelectedCompareBy((prev) =>
      segments.some((segment) => segment.quotaGroupRef === prev) ? prev : segments[0]?.quotaGroupRef
    );
  }, [segments]);

  useEffect(() => {
    if (!selectedCompareBy) return;
    setPagesByTab((prev) => ({
      ...prev,
      [selectedCompareBy]: 1,
    }));
  }, [selectedCompareBy]);

  const onPageChange = (event) => {
    const results = activeSegment?.responses ?? [];
    handlePageClick({
      event,
      items: results,
      itemsPerPage: PAGE_SIZE,
      setOffset: (offset) => {
        const nextPage = Math.floor(offset / PAGE_SIZE) + 1;
        setPagesByTab((prev) => ({
          ...prev,
          [selectedCompareBy]: nextPage,
        }));
      },
    });
  };

  const handleClosePlayer = () => {
    setSelectedVideoIndex(null);
  };

  const allResponses = activeSegment?.responses ?? [];
  const selectedVideo = selectedVideoIndex !== null ? allResponses[selectedVideoIndex] : null;
  const hasPrevious = selectedVideoIndex > 0;
  const hasNext = selectedVideoIndex !== null && selectedVideoIndex < allResponses.length - 1;
  const isPlayerOpen = selectedVideoIndex !== null;

  const handlePreviousVideo = () => {
    setSelectedVideoIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
  };

  const handleNextVideo = () => {
    setSelectedVideoIndex((prev) =>
      prev !== null && prev < allResponses.length - 1 ? prev + 1 : prev
    );
  };

  return (
    <>
      <Tabs
        onChange={setSelectedCompareBy}
        options={[
          ...segments.map(({ compareBy, quotaGroupRef }) => ({
            label: compareBy,
            value: quotaGroupRef,
          })),
          { label: 'Bookmarks', value: 'bookmarks' },
          { label: 'Favorites', value: 'favorites' },
        ]}
        value={selectedCompareBy}
      />
      {renderResponseTextSummary &&
        activeSegment &&
        renderResponseTextSummary({ question, segment: activeSegment })}
      {selectedCompareBy === 'bookmarks' && totalResponses === 0 && (
        <Layout.Flex.Column
          className={styles['empty-state']}
          data-testid="video-bookmarks-empty-state"
        >
          <BookmarkIcon />
          <p>No bookmarks</p>
          <p>You can save bookmarks on individual videos</p>
        </Layout.Flex.Column>
      )}
      {selectedCompareBy === 'favorites' && totalResponses === 0 && (
        <Layout.Flex.Column
          className={styles['empty-state']}
          data-testid="video-favorites-empty-state"
        >
          <BookmarkIcon />
          <p>No favorites</p>
          <p>You can save favorites on individual videos</p>
        </Layout.Flex.Column>
      )}
      {activeSegment && totalResponses > 0 && (
        <Layout.Flex.Column as="ul">
          {pagedResponses.map((response, pageIndex) => {
            const globalIndex = (currentPage - 1) * PAGE_SIZE + pageIndex;
            const responseBookmarks = response.bookmarks || [];
            const hasBookmarks = responseBookmarks.length > 0;
            const isExpanded = expandedBookmarks[response.responseId] || false;
            const visibleBookmarks = isExpanded ? responseBookmarks : responseBookmarks.slice(0, 3);
            const hasMoreBookmarks = responseBookmarks.length > 3;
            const isFavorited = favorites.some(
              (fav) =>
                fav.survey_response === response.responseId && fav.question_id === question?.id
            );

            return (
              <li key={response.responseId}>
                <Card
                  className={styles['video-card-response-container']}
                  onClick={() => {
                    setSelectedVideoIndex(globalIndex);
                  }}
                >
                  <Layout.Flex.Row className={styles['video-card-content']}>
                    {response.thumbnailUrl && (
                      <img
                        alt="Video thumbnail"
                        className={styles['video-card-thumbnail']}
                        src={response.thumbnailUrl}
                      />
                    )}
                    <div className={styles['video-card-summary']}>
                      <p className={styles['video-card-summary-text']}>{response.aiSummary}</p>
                      <p className={styles['video-ai-summary-text']}>
                        Summary generated by Numerator AI
                      </p>
                      {hasBookmarks && (
                        <div
                          className={styles['bookmarks-blurb']}
                          data-testid="video-card-bookmarks-blurb"
                        >
                          <div className={styles['bookmarks-header']}>
                            <BookmarkIcon />
                            <span className={styles['bookmarks-title']}>BOOKMARKS</span>
                          </div>
                          {visibleBookmarks.map((bookmark) => (
                            <div
                              className={styles['bookmark-item']}
                              data-testid="video-card-bookmark-item"
                              key={bookmark.id}
                            >
                              <span className={styles['bookmark-timestamp']}>
                                {formatTimestamp(bookmark.start_ts)}
                              </span>
                              <span className={styles['bookmark-text']}>{bookmark.text}</span>
                            </div>
                          ))}
                          {hasMoreBookmarks && !isExpanded && (
                            <button
                              className={styles['bookmark-show-more']}
                              data-testid="video-card-bookmark-show-more"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedBookmarks((prev) => ({
                                  ...prev,
                                  [response.responseId]: true,
                                }));
                              }}
                              type="button"
                            >
                              ...show more
                            </button>
                          )}
                        </div>
                      )}
                      <div
                        className={styles['favorite-toggle-wrapper']}
                        data-testid="video-card-favorite-toggle"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FavoriteToggle
                          isFavorite={isFavorited}
                          onChange={() => handleToggleFavorite(response.responseId)}
                        />
                      </div>
                    </div>
                  </Layout.Flex.Row>
                </Card>
              </li>
            );
          })}
          <Pagination onPageChange={onPageChange} pageCount={pageCount} show={pageCount > 1} />
        </Layout.Flex.Column>
      )}

      {selectedVideo && (
        <ModalV2 isActive={isPlayerOpen} onClose={handleClosePlayer} size="fullscreen">
          <VideoPlayer
            hasNext={hasNext}
            hasPrevious={hasPrevious}
            onBookmarkChange={handleBookmarkChange}
            onNext={handleNextVideo}
            onPrevious={handlePreviousVideo}
            questionId={question?.id}
            responseId={selectedVideo.responseId}
            videoData={{
              ai_summary: selectedVideo.aiSummary,
              audio_segments: selectedVideo.audioSegments,
              items: selectedVideo.items,
              video_url: selectedVideo.videoUrl,
            }}
          />
        </ModalV2>
      )}
    </>
  );
};

Video.propTypes = {
  data: PropTypes.object.isRequired,
  question: PropTypes.object,
  renderResponseTextSummary: PropTypes.func,
  surveyRef: PropTypes.number,
};

export default Video;
